import { WalletProviderE } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import React, { useEffect, useMemo } from 'react';
import { CURRENCY_SYMBOL, walletCoins } from 'src/constants/common.constants';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import NftCardAuctionTime from 'src/features/common/collectible/components/nft-card-auction-time';
import {
    NotOnSaleIcon,
    StyledAuction,
    StyledFiatPrice,
    StyledNftCardMedia,
    StyledPrices,
} from 'src/features/common/collectible/components/nft-card/nft-card.styles';
import { INftCardLocale, IProps } from 'src/features/common/collectible/components/nft-card/nft-card.types';
import {
    StyledBottom,
    StyledBottomButton,
    StyledCardWrapper,
    StyledCountDown,
    StyledCreatorName,
    StyledFeatures,
    StyledName,
    StyledPositionName,
    StyledRainDrop1,
    StyledRainDrop2,
    StyledSubTitle,
    StyledTypeWrapper,
} from 'src/features/white-labels/ninja/collectible/components/nft-card/nft-card.styles';
import { useGetNftSales, useNftSales } from 'src/features/common/collectible/hooks/nft-sales.hooks';
import { useCollectionsListEntities } from 'src/features/common/collection/hooks/use-collections-list';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useBlockChainsById } from 'src/features/common/wallet/hooks/wallet.hooks';
import { useCollectiblePrice } from 'src/hooks/useCollectiblePrice.hook';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { SvgIconBold } from 'src/ui/common/atoms/svg-icon/svg-icon.types';
import { Typography } from 'src/ui/common/atoms/typography';
import { getTokenTypeIcon } from 'src/ui/common/organisms/resource-uploaders/uploaded-file-preview/helpers';
import { formatToMillionGroup } from 'src/utils/entity.utils';
import { decodeHTMLEntities } from 'src/utils/html.utils';
import { getNftTypeByStandard } from 'src/utils/lot.utils';
import { useTheme } from 'styled-components';

export const NftCard: React.VFC<IProps> = ({ preload, nft, variant = 'dark', testId }) => {
    const { t } = useTranslation(['components', 'pages']);
    const screen = useScreenDetect();
    const userListEntities = useUserListEntities();
    const collectionsEntities = useCollectionsListEntities();
    const { isSingle } = getNftTypeByStandard(nft.standard);
    const copies = nft.copies ? +nft.copies : 0;
    const creator = userListEntities[nft.author];
    const owner = userListEntities[nft.owner];
    const nftSales = useNftSales();
    const getNftSales = useGetNftSales();

    const locale: INftCardLocale = useMemo(
        () => ({
            ...t('bidCard', { ns: 'components', returnObjects: true }),
            userRoles: t('nft.view.userRoles', { ns: 'pages', returnObjects: true }),
        }),
        [t]
    );

    const link = generatePath(RouteE.COLLECTIBLE_DETAILS, {
        id: nft.id,
    });
    const currentNftSales = nftSales[nft.id]?.status === 'completed' ? nftSales[nft.id] : undefined;

    const tokenIcon = useMemo(() => getTokenTypeIcon({ fileUrl: nft.nftUri, iconVariant: 'bold' }), [nft.nftUri]);

    const creatorName = useDisplayName(creator);
    const ownerName = useDisplayName(owner);
    const collection = nft.collectionId && collectionsEntities[nft.collectionId];
    const blockchains = useBlockChainsById();
    const theme = useTheme();

    useEffect(() => {
        getNftSales([nft.id]);
    }, [getNftSales, nft.id]);

    const isBurnt = copies === 0;
    const isNotMinted = !isBurnt && !nft.isPublished;
    const { price, fiatPrice, fiatCurrency } = useCollectiblePrice(nft, locale, currentNftSales);

    const subTitle = useMemo(() => {
        if (nft.collectionId) {
            return {
                tooltip: `${t('nft.collection', { ns: 'components' })}`,
                link: generatePath(RouteE.COLLECTION_VIEW, { id: nft.collectionId }),
                label: collection ? collection.name : '',
            };
        }
        if (isSingle) {
            return {
                tooltip: `${t('nft.owner', { ns: 'components' })}`,
                link: generatePath(RouteE.PROFILE, { login: owner?.login }),
                label: ownerName,
            };
        }
        return {
            tooltip: `${t('nft.author', { ns: 'components' })}`,
            link: generatePath(RouteE.PROFILE, { login: creator?.login }),
            label: creatorName,
        };
    }, [nft.collectionId, isSingle, t, creatorName, creator?.login, collection, ownerName, owner?.login]);

    const priceLabel = useMemo(() => {
        if (isNotMinted) {
            return locale.notMinted;
        }
        if (isBurnt) {
            return locale.burnt;
        }
        return price;
    }, [isBurnt, isNotMinted, locale, price]);

    return (
        <Link href={link} prefetch={false}>
            <a rel="canonical">
                <StyledCardWrapper data-test-id={testId}>
                    {tokenIcon && (
                        <StyledTypeWrapper>
                            <SvgIcon
                                variant="bold"
                                icon={tokenIcon as SvgIconBold['icon']}
                                height={screen.xs ? 19 : 20}
                                width={screen.xs ? 19 : 20}
                            />
                        </StyledTypeWrapper>
                    )}
                    <StyledName
                        tag="div"
                        size="xs"
                        variant="title"
                        cardVariant={variant}
                        overflowLines={1}
                        altTitle={decodeHTMLEntities(nft.name)}
                        withCopies={copies > 1}
                        medium
                    >
                        <>
                            {decodeHTMLEntities(nft.name)}
                            {copies > 1 ? (
                                <div>
                                    <i title={`Editions: ${copies}`}>{copies}</i>
                                </div>
                            ) : (
                                ''
                            )}
                        </>
                    </StyledName>
                    <StyledFeatures>
                        <StyledNftCardMedia nft={nft} preload={preload} />
                        <StyledCountDown>
                            <span className="js-countdown" data-timer="55555" data-labels=" ,  h , m , s " />
                        </StyledCountDown>
                        <StyledRainDrop1>
                            <img src="/icons/rain1.svg" alt="rain" />
                        </StyledRainDrop1>
                        <StyledRainDrop2>
                            <img src="/icons/rain2.svg" alt="star" />
                        </StyledRainDrop2>
                    </StyledFeatures>
                    <StyledBottom>
                        <StyledSubTitle>
                            {!!subTitle.link && (
                                <Link href={subTitle.link} prefetch={false}>
                                    <a rel="canonical">
                                        <StyledPositionName>{decodeHTMLEntities(subTitle.tooltip)}</StyledPositionName>
                                        <StyledCreatorName overflowLines={1} size="xxs">
                                            {decodeHTMLEntities(subTitle.label)}
                                        </StyledCreatorName>
                                    </a>
                                </Link>
                            )}
                        </StyledSubTitle>
                        <StyledPrices notOnSale={!currentNftSales?.isOnSale} notMinted={isNotMinted} variant="dark">
                            {nftSales[nft.id]?.isAuction && (
                                <StyledAuction alignItems="center">
                                    <SvgIcon variant="bold" icon="auction" />
                                    <NftCardAuctionTime
                                        type="Card"
                                        start={nftSales[nft.id]?.auctionStartAt!}
                                        end={nftSales[nft.id]?.auctionEndAt!}
                                    />
                                </StyledAuction>
                            )}
                            {!nftSales[nft.id]?.isAuction && (
                                <FlexBox alignItems="center">
                                    {currentNftSales?.isOnSale ? (
                                        <Spacing mr="4px">
                                            <Typography size="xs" medium color="blue600" bold>
                                                {walletCoins[blockchains[nft.blockchainId]?.name as WalletProviderE]}
                                            </Typography>
                                        </Spacing>
                                    ) : (
                                        <NotOnSaleIcon
                                            color={
                                                !nft.isPublished && nft.copies
                                                    ? theme.colors.pink500
                                                    : theme.colors.gs200
                                            }
                                            variant="regular"
                                            icon="hamburger"
                                        />
                                    )}
                                    <Typography size="xs" medium>
                                        {formatToMillionGroup(priceLabel)}
                                    </Typography>
                                </FlexBox>
                            )}
                            {fiatPrice && !nftSales[nft.id]?.isAuction && (
                                <StyledFiatPrice alignItems="center">
                                    {CURRENCY_SYMBOL[fiatCurrency]}
                                    {formatToMillionGroup(fiatPrice)}
                                </StyledFiatPrice>
                            )}
                        </StyledPrices>
                    </StyledBottom>
                    <StyledBottomButton>
                        <Link href={link} prefetch={false}>
                            <a>
                                <Button stretch variant="rounded">
                                    {t('crud.goToNFTPage', { ns: 'actions' })}
                                </Button>
                            </a>
                        </Link>
                    </StyledBottomButton>
                </StyledCardWrapper>
            </a>
        </Link>
    );
};
